import { computed, observable, runInAction } from "mobx"
import { toast } from "sonner"

import { Model } from "@/lib/Model.ts"
import { validateEmail } from "@/lib/validateUtils.ts"
import { ellipsize } from "@/styles/textStyles.ts"

import type { InvitationLandingContext, ProjectKey, TID } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"


export class InviteHelpDialogModel extends Model {
  readonly email = observable.box("")
  readonly emailTouched = observable.box(false)

  readonly problem = observable.box("")
  readonly problemTouched = observable.box(false)

  readonly submitting = observable.box(false)
  readonly error = observable.box<Error | null>(null)

  readonly isDeveloperMode = computed(() => this.mode === "developer")

  readonly emailValid = computed(() => validateEmail(this.email.get()))
  readonly problemValid = computed(() => this.problem.get().trim() !== "")

  readonly disabled = computed(() => {
    return this.submitting.get() || (this.isDeveloperMode.get()
      ? (this.email.get().trim() === "" || !this.emailValid.get())
      : !this.problemValid.get())
  })

  readonly submitButtonText = computed(() => {
    return this.isDeveloperMode.get()
      ? (this.submitting.get() ? ellipsize("Sending") : "Send invite")
      : (this.submitting.get() ? ellipsize("Requesting") : "Request support")
  })

  readonly problemPlaceholder = computed(() => {
    return this.isDeveloperMode.get() ? "Describe the problem (optional)" : "Describe the problem"
  })

  constructor(
    readonly context: OrgContext,
    readonly mode: "developer" | "support",
    private readonly projectKey?: ProjectKey,
    private readonly taskId?: TID
  ) {
    super("InviteHelpDialogModel")
  }

  readonly setEmail = (value: string) => {
    runInAction(() => this.email.set(value))
  }

  readonly setProblem = (value: string) => {
    runInAction(() => this.problem.set(value))
  }

  readonly markEmailTouched = () => {
    runInAction(() => this.emailTouched.set(this.email.get().trim() !== ""))
  }

  readonly markProblemTouched = () => {
    runInAction(() => this.problemTouched.set(this.problem.get().trim() !== ""))
  }

  private readonly submitDeveloperInvitation = async (): Promise<void> => {
    if (!this.emailValid.get()) {
      runInAction(() => this.emailTouched.set(true))
      throw new Error("Invalid email")
    }

    if (!this.projectKey || !this.taskId) {
      throw new Error("Project information is not available")
    }

    const landingContext: InvitationLandingContext = {
      projectKey: this.projectKey,
      taskId: this.taskId
    }

    await this.context.queries.apiClient.createInvitation(
      this.email.get().trim(),
      this.projectKey,
      "Member",
      "Member",
      landingContext,
      this.problem.get().trim() || null
    )

    toast.success(`Invitation sent to ${this.email.get()}`)
  }

  private readonly submitSupportRequest = async (): Promise<void> => {
    if (!this.problemValid.get()) {
      runInAction(() => this.problemTouched.set(true))
      throw new Error("Problem description is required")
    }

    if (!this.taskId) {
      throw new Error("Task information is not available")
    }

    await this.context.queries.apiClient.inviteMatterSupport(
      this.problem.get().trim(),
      this.taskId
    )

    toast.success("Support request sent")
  }

  readonly submit = async (onSuccess: () => void): Promise<boolean> => {
    runInAction(() => {
      this.submitting.set(true)
      this.error.set(null)
    })

    try {
      if (this.isDeveloperMode.get()) {
        await this.submitDeveloperInvitation()
      } else {
        await this.submitSupportRequest()
      }

      onSuccess()
      return true
    } catch (e) {
      const errorObj = e instanceof Error ? e : new Error(String(e))
      runInAction(() => this.error.set(errorObj))
      return false
    } finally {
      runInAction(() => this.submitting.set(false))
    }
  }
}
